import { api } from "../api";
import { useAsync } from "../hooks";
import { navigate } from "../router";
import { RefreshButton } from "./RefreshButton";
import { fmt } from "../format";
import type { MyStats, Scraper, ChangeFeedItem } from "../types";

/** A scraper name that opens its detail (/scrapers/:id) on click — plain click SPA-navigates,
 *  cmd/ctrl-click still opens a new tab. Used in the Home dashboard's Next runs + Recent activity. */
function ScraperLink({ id, name }: { id: string; name: string }) {
  return (
    <a
      className="link-btn"
      href={`/scrapers/${id}`}
      onClick={(e) => {
        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(`/scrapers/${id}`);
      }}
    >
      {name}
    </a>
  );
}

/**
 * The customer's home — their own fleet at a glance (tenant-scoped via GET /me/stats). Same shape as
 * the operator Overview, but for one org: scrapers, 24h runs, healing. Landing page for a signed-in
 * customer (operators land on the platform Overview instead).
 */
export function Home({ onNav }: { onNav: (tab: "new") => void }) {
  const { data, loading, error, reload } = useAsync<MyStats>(() => api("/me/stats"), [], 30_000);
  const { data: fleet } = useAsync<{ scrapers: Scraper[] }>(
    async () => ({ scrapers: await api<Scraper[]>("/scrapers") }),
    [],
    30_000,
  );
  // Recent changes: one cheap fleet-wide feed (persisted deltas), not N on-demand storage diffs.
  const { data: changeFeed } = useAsync<{ changes: ChangeFeedItem[] }>(() => api("/me/changes"), [], 30_000);

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  if (!data) return null;

  const { scrapers, runs24h, heals24h } = data;
  const clean = runs24h.total > 0 ? Math.round((runs24h.success / runs24h.total) * 100) : null;

  const all = fleet?.scrapers ?? [];
  const changes = changeFeed?.changes ?? [];
  // Upcoming: active scrapers with a scheduled next run, soonest first.
  const nextRuns = all
    .filter((s) => s.status === "active" && s.next_run_at)
    .sort((a, b) => (a.next_run_at ?? "").localeCompare(b.next_run_at ?? ""))
    .slice(0, 5);
  // Recent: whatever ran most recently, newest first.
  const recent = all
    .filter((s) => s.last_run_at)
    .sort((a, b) => (b.last_run_at ?? "").localeCompare(a.last_run_at ?? ""))
    .slice(0, 5);

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Home</h1>
        <RefreshButton onClick={reload} />
      </div>

      {scrapers.total === 0 ? (
        <div className="empty">
          No scrapers yet. Create your first one — name a page and the fields, and Amendie starts
          collecting on a schedule, repairing the extractor itself when the page changes.
          <div className="row-gap" style={{ marginTop: "var(--sp4)" }}>
            <button className="btn" onClick={() => onNav("new")}>
              New scraper →
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="fleet-line lead">
            <em>{scrapers.total}</em> scraper{scrapers.total === 1 ? "" : "s"} · <em>{scrapers.active}</em>{" "}
            collecting
            {scrapers.paused > 0 ? ` · ${scrapers.paused} paused` : ""}
          </p>

          <section>
            <h3>Runs · last 24 hours</h3>
            <div className="health-bar">
              <span className="hb-item">
                <b>{runs24h.total}</b> run{runs24h.total === 1 ? "" : "s"}
              </span>
              {clean !== null && (
                <span className="hb-item">
                  <b>{clean}%</b> clean
                </span>
              )}
              {runs24h.failed > 0 && (
                <span className="hb-item bad">
                  <b>{runs24h.failed}</b> failed
                </span>
              )}
            </div>
            {runs24h.total === 0 && <p className="muted sm">No runs in the last 24 hours.</p>}
          </section>

          {heals24h.attempts > 0 && (
            <section>
              <h3>Healing · last 24 hours</h3>
              <div className="health-bar">
                {heals24h.healed > 0 && (
                  <span className="hb-item gold">
                    <b>{heals24h.healed}</b> repaired
                  </span>
                )}
                {heals24h.failed > 0 && (
                  <span className="hb-item bad">
                    <b>{heals24h.failed}</b> unrepaired
                  </span>
                )}
                <span className="hb-item">
                  <b>{heals24h.attempts}</b> attempt{heals24h.attempts === 1 ? "" : "s"}
                </span>
              </div>
            </section>
          )}

          {nextRuns.length > 0 && (
            <section>
              <h3>Next runs</h3>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Scraper</th>
                      <th>Scheduled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nextRuns.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <ScraperLink id={s.id} name={s.name} />
                        </td>
                        <td className="nowrap">{s.next_run_at ? fmt(s.next_run_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {recent.length > 0 && (
            <section>
              <h3>Recent activity</h3>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Scraper</th>
                      <th>Status</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <ScraperLink id={s.id} name={s.name} />
                        </td>
                        <td>
                          <span className={s.last_run_status ? `pill ${s.last_run_status}` : "pill"}>
                            {s.last_run_status ?? "—"}
                          </span>
                        </td>
                        <td className="nowrap">{s.last_run_at ? fmt(s.last_run_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {changes.length > 0 && (
            <section>
              <h3>Recent changes</h3>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Scraper</th>
                      <th className="num">Added</th>
                      <th className="num">Removed</th>
                      <th className="num">Changed</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map((c) => (
                      <tr key={c.runId}>
                        <td>{c.scraperName}</td>
                        <td className="num">{c.added}</td>
                        <td className="num">{c.removed}</td>
                        <td className="num">{c.changed}</td>
                        <td className="nowrap">{fmt(c.computedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
